"use client";
import { useRouter, usePathname } from "next/navigation";
import { useCallback, useState, type MouseEvent, type ReactNode } from "react";

import {
  Bell,
  Goal,
  Plus,
  Minus,
  Telescope,
  TrendingUp,
  Star,
  BookOpen,
  Rss,
  Sparkles,
  Library,
  Hammer,
  Terminal,
  Activity,
  Filter,
} from "lucide-react";
import useSWR from "swr";
import { PencilEditIcon } from "@/components/icons";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { SidebarHistory } from "@/components/sidebar-history";
import {
  SidebarAlerts,
  SUBSCRIBED_SWR_KEY,
} from "@/components/sidebar-alerts";
import { SidebarSkills } from "@/components/sidebar-skills";
import { apiClient } from "@/lib/api-client";
import { SearchDialog } from "@/components/search-dialog";
import { SidebarUserLink } from "@/components/sidebar-user-link";
import { NavSocial } from "@/components/nav-social";
import { useAgentLaunch } from "@/lib/agent-launch";
import { telemetry } from "@/lib/telemetry";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";

import Link from "next/link";
import Image from "next/image";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";

// Sample threads data
const threadsData = {
  threads: [
    {
      title: "Threads",
      type: "all",
    },
  ],
};

type SidebarConfirmAction =
  | "createSkill"
  | "createAlert"
  | "subscribeAlerts";

function HeaderAction({
  label,
  tone,
  onClick,
  children,
}: {
  label: string;
  tone: "emerald" | "amber" | "sky" | "violet";
  onClick: (event: MouseEvent<HTMLSpanElement>) => void;
  children: ReactNode;
}) {
  const toneClass = {
    emerald: "text-emerald-300 hover:bg-emerald-400/10 hover:ring-emerald-400/35",
    amber: "text-amber-300 hover:bg-amber-400/10 hover:ring-amber-400/35",
    sky: "text-sky-300 hover:bg-sky-400/10 hover:ring-sky-400/35",
    violet: "text-violet-300 hover:bg-violet-400/10 hover:ring-violet-400/35",
  }[tone];

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      title={label}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(event as unknown as MouseEvent<HTMLSpanElement>);
        }
      }}
      className={cn(
        "flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded-md ring-1 ring-inset ring-white/10 transition-colors",
        toneClass,
      )}
    >
      {children}
    </span>
  );
}

export function AppSidebar() {
  const router = useRouter();
  const pathname = usePathname();
  const { setOpenMobile } = useSidebar();
  const { user } = useAuth();
  const launch = useAgentLaunch();

  // Open a specific library tab. stopPropagation so the click doesn't also
  // toggle the section it sits in.
  const openLibrary = useCallback(
    (href: string) => {
      setOpenMobile(false);
      router.push(href);
    },
    [router, setOpenMobile]
  );

  // "Create a skill" → the built-in Create a Skill builder. Auto-sends a
  // kickoff so the agent immediately asks what to build; skillRun raises the
  // turn cap so the interview + draft never gets cut off.
  const createSkillFlow = useCallback(() => {
    setOpenMobile(false);
    launch({
      skillId: "create-a-skill",
      skillName: "Create a Skill",
      skillRun: true,
      query: "Help me build a new skill.",
    });
  }, [launch, setOpenMobile]);

  // "Create alert" → lead to the agent with a kickoff so it starts the alert
  // flow right away. TODO(create-alert-skill): once the dedicated "Create an
  // alert" skill exists, launch it here with skillId instead of a raw query
  // (mirrors createSkillFlow). Tracked in memory + the create-alert-skill todo.
  const createAlert = useCallback(() => {
    setOpenMobile(false);
    launch({ query: "Help me create a new alert." });
  }, [launch, setOpenMobile]);

  // Small per-section deep link into the library (sits in the header row).
  // Styled as a clear icon-BUTTON (ring + hover bg) so it reads as a tappable
  // "open the library" affordance, not just decoration.
  const LibLink = ({ href, label }: { href: string; label: string }) => (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        openLibrary(href);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          openLibrary(href);
        }
      }}
      className="flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/[0.07] hover:text-white"
    >
      <Library className="h-3 w-3" />
    </span>
  );

  // Favorite state for sections
  const [isResearchFavorited, setIsResearchFavorited] = useState(false);

  // Controlled open/closed state for each collapsible section. We track this in
  // React (instead of letting Radix manage it uncontrolled) so the flex layout
  // can give every OPEN section an equal share of the leftover height
  // (`flex-1`) while collapsing CLOSED sections down to just their header row
  // (`flex-none`). This is what makes open sections tile evenly and closed ones
  // stack tight with no dead black space.
  const [openSections, setOpenSections] = useState({
    terminal: true,
    research: true,
    skills: true,
    personalAlerts: true,
    subscribedAlerts: true,
  });
  const [confirmAction, setConfirmAction] =
    useState<SidebarConfirmAction | null>(null);

  const setSectionOpen = useCallback(
    (key: keyof typeof openSections, open: boolean) => {
      setOpenSections((prev) => ({ ...prev, [key]: open }));
    },
    []
  );

  const requestConfirm = useCallback(
    (action: SidebarConfirmAction) => (event: MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setConfirmAction(action);
    },
    [],
  );

  // An open section grows to share remaining height equally; a closed section
  // shrinks to its header only so the next section tiles right beneath it.
  // The min-height floor stops flexbox from squeezing an open section smaller
  // than its header + a few rows on short/zoomed viewports — without it the
  // section's content overflows its box and paints over the next section's
  // header. When the floors no longer fit, the outer overflow-y-auto container
  // scrolls instead. overflow-hidden is a hard clip so nothing can ever bleed
  // into a neighbor.
  const sectionClass = (open: boolean) =>
    open ? "flex-1 min-h-[140px] overflow-hidden" : "flex-none";

  // Whether to render the "Subscribed Alerts" section at all. Shares the
  // SWR key with SidebarAlerts(variant="subscribed") so the fetch is deduped.
  // The section is hidden entirely when the user has zero subscriptions, so
  // we never show an empty dead header.
  const { data: subscribedData } = useSWR(
    user ? SUBSCRIBED_SWR_KEY : null,
    () => apiClient.listSubscribedAlerts(),
    { revalidateOnFocus: false, dedupingInterval: 10_000 }
  );
  const hasSubscribedAlerts = (subscribedData?.count ?? 0) > 0;

  // Auto-scroll handler for the threads container
  const handleThreadsScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

      // Dispatch custom event that SidebarHistory can listen to
      if (isNearBottom) {
        window.dispatchEvent(new CustomEvent("loadMoreChats"));
      }
    },
    []
  );

  /** Go to home without polluting the URL with `?_=...` (old jQuery-style cache buster). */
  const navigateHomeFresh = useCallback(() => {
    setOpenMobile(false);
    if (pathname === "/") {
      window.dispatchEvent(new Event("xynth:new-home-chat"));
    } else {
      router.push("/");
    }
  }, [pathname, router, setOpenMobile]);

  // "New session" starts a fresh chat immediately — no confirmation modal.
  // stopPropagation is required because this button sits inside the Research
  // CollapsibleTrigger; without it the click would also toggle the section.
  const handleNewSession = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      navigateHomeFresh();
    },
    [navigateHomeFresh],
  );

  const confirmCopy = {
    createSkill: {
      title: "Create a new skill?",
      body: "Xynth will guide you through building a reusable skill.",
      action: "Create skill",
    },
    createAlert: {
      title: "Create a new alert?",
      body: "Xynth will help you build a recurring alert.",
      action: "Create alert",
    },
    subscribeAlerts: {
      title: "Browse more alerts?",
      body: "Open the library to subscribe to more signals and automations.",
      action: "Browse alerts",
    },
  } satisfies Record<
    SidebarConfirmAction,
    { title: string; body: string; action: string }
  >;

  const runConfirmedAction = useCallback(() => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === "createSkill") createSkillFlow();
    if (action === "createAlert") createAlert();
    if (action === "subscribeAlerts") openLibrary("/library?tab=alerts");
  }, [confirmAction, createAlert, createSkillFlow, openLibrary]);

  return (
    <>
    <Sidebar data-tour="sidebar-root">
      <SidebarHeader>
        <SidebarMenu className="gap-1">
          <SidebarMenuItem>
            <div className="flex items-center justify-between px-2 py-2">
              <button
                type="button"
                data-tour="sidebar-home-logo"
                onClick={navigateHomeFresh}
                className="flex items-center gap-2.5 cursor-pointer"
              >
                <div className="flex aspect-square size-9 items-center justify-center rounded-lg overflow-hidden">
                  <Image
                    src="/logo.png"
                    alt="Xynth Logo"
                    width={36}
                    height={36}
                    className="size-full object-contain"
                  />
                </div>
                <span className="text-lg font-semibold tracking-tight">Xynth</span>
              </button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="flex flex-col">
        {/* Research + Alerts sections share the padded content area.
            Research takes flex-1 (fills remaining height, scrolls its chat
            list). Alerts sits below with its own capped scroll region so
            a long alert list never pushes the Research section off-screen. */}
        <div
          className="flex-1 flex flex-col px-4 min-h-0 overflow-y-auto pt-1 md:pt-2 gap-2"
          style={{ paddingBottom: "20px" }}
        >
          {/* Terminal */}
          <Collapsible
            open={openSections.terminal}
            onOpenChange={(o) => setSectionOpen("terminal", o)}
            className={`group/collapsible flex flex-col mt-1 md:mt-0 ${sectionClass(
              openSections.terminal
            )}`}
          >
            <SidebarMenu className="shrink-0">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    className="text-sm flex items-center justify-between w-full !transition-none !pr-2"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Terminal size={16} className="text-emerald-400" />
                      <span className="truncate">Terminal</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <div
                        className="size-4 flex items-center justify-center shrink-0"
                        style={{ minWidth: "16px", maxWidth: "16px" }}
                      >
                        <Plus
                          className="group-data-[state=open]/collapsible:hidden"
                          size={14}
                        />
                        <Minus
                          className="group-data-[state=closed]/collapsible:hidden"
                          size={14}
                        />
                      </div>
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
              </SidebarMenuItem>
            </SidebarMenu>
            <CollapsibleContent className="flex-none">
              {/* Mirrors the Research section geometry: thread line at left-4,
                  content indented pl-4, so sub-items line up with the other
                  sections' contents. */}
              <div className="relative pb-1 pt-1">
                <div className="absolute left-4 inset-y-0 w-px bg-gradient-to-b from-emerald-500/50 via-emerald-500/20 to-transparent" />
                <div className="flex flex-col gap-0.5 pl-4">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        href="/flow"
                        className="flex items-center gap-2 rounded-md py-1.5 px-2 text-[13px] text-muted-foreground hover:bg-white/[0.05] hover:text-white transition-colors"
                        onClick={() => setOpenMobile(false)}
                      >
                        <Activity size={14} className="shrink-0" />
                        <span className="truncate">Options Flow</span>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[220px] text-[12px] leading-snug">
                      Every options trade as it prints, live. Sweeps, blocks
                      and floor trades with premium, size and Greeks.
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        href="/options-screener"
                        className="flex items-center gap-2 rounded-md py-1.5 px-2 text-[13px] text-muted-foreground hover:bg-white/[0.05] hover:text-white transition-colors"
                        onClick={() => setOpenMobile(false)}
                      >
                        <Filter size={14} className="shrink-0" />
                        <span className="truncate">Options Screener</span>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[220px] text-[12px] leading-snug">
                      Scan every U.S. options contract. Filter by volume, open
                      interest, IV, premium and Greeks.
                    </TooltipContent>
                  </Tooltip>
                  <Link
                    href="/feedback"
                    className="flex items-center gap-2 rounded-md py-1.5 px-2 text-[12px] text-emerald-400/80 hover:bg-emerald-400/10 hover:text-emerald-400 transition-colors"
                    onClick={() => setOpenMobile(false)}
                  >
                    <Plus size={14} className="shrink-0" />
                    <span className="truncate">More datasets (soon)</span>
                  </Link>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Research (existing) */}
          <Collapsible
            data-tour="sidebar-research-section"
            open={openSections.research}
            onOpenChange={(o) => setSectionOpen("research", o)}
            className={`group/collapsible flex flex-col ${
              openSections.terminal ? "" : "mt-1 md:mt-0"
            } ${sectionClass(openSections.research)}`}
          >
            <SidebarMenu className="shrink-0">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    className="text-sm flex items-center justify-between w-full !transition-none !pr-2"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Telescope size={16} className="text-emerald-400" />
                      <span className="truncate">Research</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <HeaderAction
                        label="Start a new research session"
                        tone="emerald"
                        onClick={handleNewSession}
                      >
                        <PencilEditIcon size={12} />
                      </HeaderAction>
                      <Star
                        className={`h-3.5 w-3.5 cursor-pointer ${
                          isResearchFavorited
                            ? "fill-[#fbbf24] text-[#fbbf24]"
                            : "text-muted-foreground"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsResearchFavorited(!isResearchFavorited);
                        }}
                      />
                      <LibLink href="/library" label="Open the library" />
                      <div
                        className="size-4 flex items-center justify-center shrink-0"
                        style={{ minWidth: "16px", maxWidth: "16px" }}
                      >
                        <Plus
                          className="group-data-[state=open]/collapsible:hidden"
                          size={14}
                        />
                        <Minus
                          className="group-data-[state=closed]/collapsible:hidden"
                          size={14}
                        />
                      </div>
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
              </SidebarMenuItem>
            </SidebarMenu>
            <CollapsibleContent className="flex-1 min-h-0">
              <div className="flex h-full flex-col">
                <div className="px-1 pb-2 pt-1">
                  <SearchDialog />
                </div>
                <div className="relative flex-1 min-h-0">
                  {/* Thread line — green accent for Research */}
                  <div className="absolute left-4 inset-y-0 w-px bg-gradient-to-b from-emerald-500/50 via-emerald-500/20 to-transparent" />
                  <div
                    className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 pl-4 pt-0 h-full"
                    onScroll={handleThreadsScroll}
                  >
                    <SidebarHistory
                      sortBy={isResearchFavorited ? "favorites" : "all"}
                    />
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Skills — the user's personal skill library. Amber accent. */}
          <Collapsible
            data-tour="sidebar-skills-section"
            open={openSections.skills}
            onOpenChange={(o) => setSectionOpen("skills", o)}
            className={`group/collapsible flex flex-col ${sectionClass(
              openSections.skills
            )}`}
          >
            <SidebarMenu className="shrink-0">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton className="text-sm flex items-center justify-between w-full !transition-none !pr-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Sparkles size={16} className="text-amber-400" />
                      <span className="truncate">Skills</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <HeaderAction
                        label="Create a skill"
                        tone="amber"
                        onClick={requestConfirm("createSkill")}
                      >
                        <Hammer className="h-3 w-3" />
                      </HeaderAction>
                      <LibLink href="/library?tab=skill" label="Browse skills in the library" />
                      <div
                        className="size-4 flex items-center justify-center shrink-0"
                        style={{ minWidth: "16px", maxWidth: "16px" }}
                      >
                        <Plus
                          className="group-data-[state=open]/collapsible:hidden"
                          size={14}
                        />
                        <Minus
                          className="group-data-[state=closed]/collapsible:hidden"
                          size={14}
                        />
                      </div>
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
              </SidebarMenuItem>
            </SidebarMenu>
            <CollapsibleContent className="flex-1 min-h-0">
              <div className="flex h-full flex-col">
                <div className="relative flex-1 min-h-0">
                  {/* Thread line — amber accent for Skills */}
                  <div className="absolute left-4 inset-y-0 w-px bg-gradient-to-b from-amber-500/50 via-amber-500/20 to-transparent" />
                  <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 pl-4 pt-0 h-full">
                    <SidebarSkills />
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Personal Alerts — alerts the user owns. Sky-blue accent. */}
          <Collapsible
            data-tour="sidebar-alerts-section"
            open={openSections.personalAlerts}
            onOpenChange={(o) => setSectionOpen("personalAlerts", o)}
            className={`group/collapsible flex flex-col ${sectionClass(
              openSections.personalAlerts
            )}`}
          >
            <SidebarMenu className="shrink-0">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton className="text-sm flex items-center justify-between w-full !transition-none !pr-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Bell size={16} className="text-sky-400" />
                      <span className="truncate">Personal Alerts</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <HeaderAction
                        label="Create alert"
                        tone="sky"
                        onClick={requestConfirm("createAlert")}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </HeaderAction>
                      <LibLink href="/library?tab=alerts" label="Browse alerts in the library" />
                      <div
                        className="size-4 flex items-center justify-center shrink-0"
                        style={{ minWidth: "16px", maxWidth: "16px" }}
                      >
                        <Plus
                          className="group-data-[state=open]/collapsible:hidden"
                          size={14}
                        />
                        <Minus
                          className="group-data-[state=closed]/collapsible:hidden"
                          size={14}
                        />
                      </div>
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
              </SidebarMenuItem>
            </SidebarMenu>
            <CollapsibleContent className="flex-1 min-h-0">
              <div className="flex h-full flex-col">
                <div className="relative flex-1 min-h-0">
                  {/* Thread line — sky-blue accent for Personal Alerts */}
                  <div className="absolute left-4 inset-y-0 w-px bg-gradient-to-b from-sky-500/50 via-sky-500/20 to-transparent" />
                  <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 pl-4 pt-0 h-full">
                    <SidebarAlerts variant="personal" />
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Subscribed Alerts — alerts the user follows. Violet accent.
              Hidden entirely when the user has zero subscriptions. */}
          {hasSubscribedAlerts && (
            <Collapsible
              data-tour="sidebar-subscribed-alerts-section"
              open={openSections.subscribedAlerts}
              onOpenChange={(o) => setSectionOpen("subscribedAlerts", o)}
              className={`group/collapsible flex flex-col ${sectionClass(
                openSections.subscribedAlerts
              )}`}
            >
              <SidebarMenu className="shrink-0">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton className="text-sm flex items-center justify-between w-full !transition-none !pr-2">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <Rss size={16} className="text-violet-400" />
                        <span className="truncate">Subscribed Alerts</span>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <HeaderAction
                          label="Subscribe to more alerts"
                          tone="violet"
                          onClick={requestConfirm("subscribeAlerts")}
                        >
                          <Rss className="h-3 w-3" />
                        </HeaderAction>
                        <LibLink href="/library?tab=alerts" label="Browse alerts in the library" />
                        <div
                          className="size-4 flex items-center justify-center shrink-0"
                          style={{ minWidth: "16px", maxWidth: "16px" }}
                        >
                          <Plus
                            className="group-data-[state=open]/collapsible:hidden"
                            size={14}
                          />
                          <Minus
                            className="group-data-[state=closed]/collapsible:hidden"
                            size={14}
                          />
                        </div>
                      </div>
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                </SidebarMenuItem>
              </SidebarMenu>
              <CollapsibleContent className="flex-1 min-h-0">
                <div className="flex h-full flex-col">
                  <div className="relative flex-1 min-h-0">
                    {/* Thread line — violet accent for Subscribed Alerts */}
                    <div className="absolute left-4 inset-y-0 w-px bg-gradient-to-b from-violet-500/50 via-violet-500/20 to-transparent" />
                    <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 pl-4 pt-0 h-full">
                      <SidebarAlerts variant="subscribed" />
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </SidebarContent>
      <SidebarFooter>
        <SidebarUserLink />
        <NavSocial />
      </SidebarFooter>
    </Sidebar>
    <Dialog
      open={confirmAction !== null}
      onOpenChange={(open) => {
        if (!open) setConfirmAction(null);
      }}
    >
      <DialogContent className="max-w-sm border-white/10 bg-[#0c0c0e] p-6 text-white sm:rounded-xl">
        {confirmAction && (
          <>
            <DialogHeader className="text-left">
              <DialogTitle className="text-xl font-bold text-white">
                {confirmCopy[confirmAction].title}
              </DialogTitle>
              <DialogDescription className="text-[14px] text-white/90">
                {confirmCopy[confirmAction].body}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4 flex-row justify-end gap-2 sm:space-x-0">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="h-9 rounded-md px-4 text-[13px] font-medium text-white/90 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirmedAction}
                className="inline-flex h-9 items-center rounded-md bg-white px-4 text-[13px] font-medium text-black transition-colors hover:bg-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
              >
                {confirmCopy[confirmAction].action}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
